import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { calendarService } from '../lib/api-service';
import type { CalendarEntry } from '../lib/types';
import { formatTime, formatDateWithDayParts, getMonthName } from '../utils/date';
import { ChevronLeft, ChevronRight, Clock, X, Calendar as CalendarIcon, Loader2 } from 'lucide-react';
import { DocumentMeta } from '../hooks/useDocumentMeta';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';

const DAY_HEADERS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
// Max event chips shown inside a day cell before collapsing into a "+N" control.
// Cells grow taller at lg, so one extra chip fits there.
const MAX_CHIPS_MD = 3;
const MAX_CHIPS_LG = 4;

// A readable event chip (start time + title) rendered inside a day cell on md+
// screens, with an accessible hover/focus tooltip previewing the event details.
function EventChip({ entry }: { entry: CalendarEntry }) {
  const navigate = useNavigate();
  const tagColor = entry.main_tags[0]?.color;
  const accent = tagColor || 'var(--vivid-purple)';

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/eveniment/${entry.slug}`);
          }}
          className={`flex w-full items-center gap-1 rounded px-1 py-0.5 text-left transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vivid-purple)] ${
            tagColor ? '' : 'bg-[var(--vivid-purple)]/10'
          }`}
          style={tagColor ? { backgroundColor: `${tagColor}1a` } : undefined}
        >
          <span className="h-3 w-0.5 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
          <span className="shrink-0 text-[10px] font-bold" style={{ color: accent, fontFamily: 'var(--font-secondary)' }}>
            {formatTime(entry.start_datetime)}
          </span>
          <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
            {entry.title}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px]">
        <p className="font-semibold">
          {entry.title}
          {entry.day_label ? ` ${entry.day_label}` : ''}
        </p>
        <p className="mt-0.5 flex items-center gap-1 opacity-90">
          <Clock className="h-3 w-3" />
          {formatTime(entry.start_datetime)}
        </p>
        {entry.location_text && <p className="opacity-90">{entry.location_text}</p>}
        {entry.main_tags.length > 0 && (
          <p className="mt-0.5 opacity-75">{entry.main_tags.map((t) => t.name).join(' · ')}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export default function Calendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [days, setDays] = useState<Record<string, CalendarEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await calendarService.getMonth(year, month + 1);
      setDays(data.days);
    } catch {
      setDays({});
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  function prevMonth() {
    if (month === 0) { setYear(year - 1); setMonth(11); }
    else setMonth(month - 1);
    setSelectedDate(null);
  }

  function nextMonth() {
    if (month === 11) { setYear(year + 1); setMonth(0); }
    else setMonth(month + 1);
    setSelectedDate(null);
  }

  // Build calendar grid
  const firstDow = new Date(year, month, 1).getDay();
  const offset = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();

  const cells: { day: number; current: boolean; key: string }[] = [];
  for (let i = offset - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 12 : month;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, current: false, key: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true, key: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 1 : month + 2;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, current: false, key: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
  }

  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const selectedEvents = selectedDate ? (days[selectedDate] || []) : [];

  function formatSelectedDate(dateStr: string): string {
    const [y, m, d] = dateStr.split('-').map(Number);
    return formatDateWithDayParts(y, m, d);
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className="mx-auto max-w-3xl px-4 py-6 md:max-w-5xl lg:max-w-7xl lg:px-8">
      <DocumentMeta
        title="Calendar"
        description="Calendarul activităților din programul Alba Iulia — Capitala Tineretului din România 2026."
        url="/calendar"
      />
      <div className="mb-6 flex items-center justify-between">
        <button onClick={prevMonth} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100 active:scale-95">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-primary)', color: 'var(--vivid-purple)' }}>
          {getMonthName(month)} {year}
        </h2>
        <button onClick={nextMonth} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100 active:scale-95">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <div className="grid grid-cols-7 border-b bg-gray-50/50">
          {DAY_HEADERS.map((name, i) => (
            <div key={i} className="py-2.5 text-center text-xs font-bold" style={{ fontFamily: 'var(--font-primary)', color: 'var(--vivid-purple)' }}>
              {name}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--vivid-purple)]" />
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {cells.map((cell, i) => {
              const events = cell.current ? (days[cell.key] || []) : [];
              const sorted = events.length > 1
                ? [...events].sort((a, b) => a.start_datetime.localeCompare(b.start_datetime))
                : events;
              const isToday = cell.key === todayKey;
              const isSelected = cell.key === selectedDate;
              const firstColor = events.length > 0 && events[0].main_tags.length > 0
                ? events[0].main_tags[0].color
                : null;
              const overflowMd = Math.max(0, sorted.length - MAX_CHIPS_MD);
              const overflowLg = Math.max(0, sorted.length - MAX_CHIPS_LG);
              const openDay = () => setSelectedDate(cell.key === selectedDate ? null : cell.key);

              return (
                <div
                  key={i}
                  onClick={() => cell.current && openDay()}
                  className={`relative flex min-h-[64px] flex-col items-center gap-1 border-b border-r py-2 transition-colors md:min-h-[112px] lg:min-h-[132px] ${
                    !cell.current ? 'bg-gray-50/50 text-muted-foreground/40' : 'cursor-pointer hover:bg-gray-50'
                  } ${isSelected ? 'bg-[var(--vivid-purple)]/5' : ''}`}
                  style={{ borderColor: 'rgba(0,0,0,0.05)' }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      if (!cell.current) return;
                      e.stopPropagation();
                      openDay();
                    }}
                    disabled={!cell.current}
                    aria-label={cell.current ? `Vezi evenimentele din ziua ${cell.day}` : undefined}
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      isToday ? 'bg-[var(--vivid-purple)] text-white' : isSelected ? 'bg-[var(--vivid-purple)]/10 text-[var(--vivid-purple)]' : ''
                    } ${cell.current ? 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vivid-purple)]' : ''}`}
                    style={{ fontFamily: 'var(--font-primary)' }}
                  >
                    {cell.day}
                  </button>

                  {/* Mobile: compact colored dots (cells too narrow for chips) */}
                  {events.length > 0 && (
                    <div className="flex gap-0.5 md:hidden">
                      {events.length <= 3 ? (
                        events.map((_, ei) => {
                          const c = events[ei].main_tags[0]?.color || 'var(--vivid-purple)';
                          return <span key={ei} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />;
                        })
                      ) : (
                        <>
                          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: firstColor || 'var(--vivid-purple)' }} />
                          <span className="text-[8px] font-bold text-muted-foreground">+{events.length}</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* md+ : readable event chips with tooltips and a "+N" overflow */}
                  {sorted.length > 0 && (
                    <div className="hidden w-full flex-col gap-0.5 px-1 md:flex">
                      {sorted.slice(0, MAX_CHIPS_LG).map((entry, ei) => (
                        <div key={`${entry.id}-${ei}`} className={ei >= MAX_CHIPS_MD ? 'hidden lg:block' : ''}>
                          <EventChip entry={entry} />
                        </div>
                      ))}
                      {overflowMd > 0 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSelectedDate(cell.key); }}
                          aria-label={`Încă ${overflowMd} evenimente`}
                          className="rounded px-1 py-0.5 text-left text-[10px] font-semibold text-muted-foreground hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vivid-purple)] lg:hidden"
                          style={{ fontFamily: 'var(--font-secondary)' }}
                        >
                          +{overflowMd} altele
                        </button>
                      )}
                      {overflowLg > 0 && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSelectedDate(cell.key); }}
                          aria-label={`Încă ${overflowLg} evenimente`}
                          className="hidden rounded px-1 py-0.5 text-left text-[10px] font-semibold text-muted-foreground hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vivid-purple)] lg:block"
                          style={{ fontFamily: 'var(--font-secondary)' }}
                        >
                          +{overflowLg} altele
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedDate && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 md:hidden" onClick={() => setSelectedDate(null)} />
          <div className="fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-white shadow-xl md:static md:mt-4 md:max-h-none md:rounded-2xl md:border md:shadow-sm">
            <div className="flex justify-center pt-3 md:hidden">
              <div className="h-1 w-10 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-5 pb-2 pt-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)', color: 'var(--vivid-purple)' }}>
                {formatSelectedDate(selectedDate)}
              </h3>
              <button onClick={() => setSelectedDate(null)} className="rounded-full p-1.5 hover:bg-gray-100">
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="px-5 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-6">
              {selectedEvents.length === 0 ? (
                <div className="py-8 text-center">
                  <CalendarIcon className="mx-auto mb-2 h-10 w-10 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                    Niciun eveniment programat
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedEvents
                    .sort((a, b) => a.start_datetime.localeCompare(b.start_datetime))
                    .map((entry, i) => {
                      const tagColor = entry.main_tags[0]?.color || 'var(--vivid-purple)';
                      return (
                        <button
                          key={`${entry.id}-${i}`}
                          onClick={() => navigate(`/eveniment/${entry.slug}`)}
                          className="flex w-full items-start gap-3 rounded-xl border-l-4 bg-gray-50 p-3 text-left transition-all hover:bg-gray-100 active:scale-[0.98]"
                          style={{ borderLeftColor: tagColor }}
                        >
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                              {entry.title}
                              {entry.day_label && (
                                <span className="ml-1 text-xs font-normal text-muted-foreground">{entry.day_label}</span>
                              )}
                            </h4>
                            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1" style={{ fontFamily: 'var(--font-secondary)' }}>
                                <Clock className="h-3 w-3" />
                                {formatTime(entry.start_datetime)}
                              </span>
                              {entry.location_text && (
                                <span style={{ fontFamily: 'var(--font-secondary)' }}>{entry.location_text}</span>
                              )}
                            </div>
                            {entry.main_tags.length > 0 && (
                              <div className="mt-1.5 flex gap-1">
                                {entry.main_tags.map((t) => (
                                  <span key={t.id} className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ backgroundColor: `${t.color}20`, color: t.color }}>
                                    {t.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
    </TooltipProvider>
  );
}
